//External
import React, { useMemo, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactGoogleAutocomplete from "react-google-autocomplete";
//Internal
import useSheetScrollLock from "../common/useSheetScrollLock";
import { GOOGLE_API_KEY } from "../../config";
import { GOOGLE_PLACE, TOOLTIP_MESSAGE, get_cookie } from "utils";
import tooltipIcon from "../../public/assets/images/tooltip.svg";

// Pickup is a single fixed location (not user-editable), unlike delivery/
// return which are addresses the customer picks via Google Places. Exported
// so the search bars can pre-populate their location field with it directly,
// without the user needing to open this popover and hit Save first.
export const PICKUP_ADDRESS = "499 N Canon Dr, Beverly Hills, CA 90210.";
export const LOCATION_PLACEHOLDER = "Enter delivery/return address";

// cityStateSlug is set during page render (before the search bars mount),
// so prefer it over selectedCityName, which the header sets later.
const getSelectedCity = () =>
  (get_cookie("cityStateSlug") || get_cookie("selectedCityName") || "").toLowerCase();

export const isMiamiSelected = () => getSelectedCity().includes("miami");

// No cookie yet means the default (LA) home page.
export const isLASelected = () =>
  ["", "la_ca", "los angeles"].includes(getSelectedCity());

// Only LA has a predefined pickup location; elsewhere the customer enters one.
export const getPickupAddress = () => (isLASelected() ? PICKUP_ADDRESS : "");

// Mirrors pickupAddress.js's onPlaceSelected/onReturnPlaceSelected exactly,
// so this popup and the vehicle booking page resolve a picked place the same
// way. Reconstructs the address from address_components ("1 World Wy, Los
// Angeles, CA 90045") instead of using the raw autocomplete text or
// place.formatted_address - for a POI (an airport, a hotel, ...) the widget's
// own text is the full prediction description ("LAX airport (LAX), World
// Way, Los Angeles, CA, USA"), which isn't what should end up in the field.
//
// Returns both `streetAddress` (just the street, e.g. "1 World Wy") and
// `fullAddress` (the whole readable string). Callers downstream - the
// vehicle booking page's `address`/`dropoff_address` fields - expect the
// street-only piece and append city/state/zip themselves; handing them
// `fullAddress` there double-appends and produces a duplicated address.
const buildAddressFromPlace = (place) => {
  let address = "";
  let city = "";
  let postcode = "";
  let stateShortName = "";

  for (const component of place?.address_components || []) {
    const componentType = component?.types?.[0];
    switch (componentType) {
      case "plus_code":
      case "sublocality_level_1":
      case "street_number":
        address = component.short_name + " ";
        break;
      case "route":
        address += component.short_name;
        break;
      case "postal_code":
        postcode = component.long_name;
        break;
      case "locality":
        city = component.long_name;
        if (!address) address = city;
        break;
      case "administrative_area_level_1":
        stateShortName = component.short_name;
        break;
    }
    if (
      (componentType === "sublocality_level_1" || componentType === "postal_town") &&
      !city
    ) {
      city = component.long_name;
    }
  }

  let fullAddress = address;
  if (city && fullAddress !== city) fullAddress += ", " + city;
  if (stateShortName) fullAddress += ", " + stateShortName;
  if (postcode) fullAddress += " " + postcode;

  return { address: fullAddress, streetAddress: address, city, zipcode: postcode };
};

const getDefaultDraft = () => ({
  activeTab: isLASelected() ? "pickup" : "delivery",
  oneWay: false,
  pickupAddress: getPickupAddress(),
  deliveryAddress: "",
  deliveryStreetAddress: "",
  deliveryZipcode: "",
  deliveryCity: "",
  returnAddress: "",
  returnStreetAddress: "",
  returnZipcode: "",
  returnCity: "",
});

/*******************
@Purpose : Location popover for the homepage reservation search bar and the
           search-results page toolbar (pickup/delivery tabs, one-way,
           addresses). Same content/behavior everywhere - `mobileSheet`
           only swaps the outer presentation from a floating anchored
           popover (desktop pill, search-results toolbar on desktop) to a
           full-screen bottom sheet with a dimmed backdrop
           (MobileReservationSearchBar, search-results toolbar on mobile).
@Parameter : {onClose, onSave, initialDraft, mobileSheet, placement}
@Author : INIC
******************/
function LocationPopover({ onClose, onSave, initialDraft, mobileSheet, placement = "above" }) {
  const isMiami = isMiamiSelected();
  const pickupAddress = getPickupAddress();
  const popoverRef = useRef(null);

  useSheetScrollLock(Boolean(mobileSheet));

  const tooltipPopperConfig = () => ({
    modifiers: {
      preventOverflow: {
        boundariesElement: popoverRef.current,
        padding: 8,
      },
      flip: {
        boundariesElement: popoverRef.current,
      },
    },
  });

  // A carried-over pickup draft can't apply outside LA - fall back to delivery.
  const [saved, setSaved] = useState(() =>
    initialDraft && (pickupAddress || initialDraft.activeTab !== "pickup")
      ? initialDraft
      : getDefaultDraft()
  );
  const [draft, setDraft] = useState(saved);
  // Address inputs are uncontrolled (defaultValue) - bump to remount them on reset.
  const [resetKey, setResetKey] = useState(0);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(saved),
    [draft, saved]
  );

  const isPrimaryAddressFilled =
    draft.activeTab === "delivery"
      ? Boolean(draft.deliveryAddress.trim())
      : Boolean(draft.pickupAddress.trim());
  const isReturnAddressFilled =
    !draft.oneWay || Boolean(draft.returnAddress.trim());
  const isSaveEnabled = isPrimaryAddressFilled && isReturnAddressFilled;

  /*******************
  @purpose : revert unsaved changes back to last saved draft
  @Author : INIC
  ******************/
  const handleReset = () => {
    setDraft(saved);
    setResetKey((k) => k + 1);
  };

  /*******************
  @purpose : persist draft and hand the display text back to the pill
  @Author : INIC
  ******************/
  const handleSave = () => {
    if (!isSaveEnabled) return;
    setSaved(draft);
    const display =
      draft.activeTab === "delivery"
        ? draft.deliveryAddress
        : draft.pickupAddress;
    onSave(display, draft);
  };

  const oneWayTooltip = (props) => (
    <Tooltip id="one-way-tooltip" className="location-tooltip" {...props}>
      {TOOLTIP_MESSAGE.ONE_WAY_LOCATION}
    </Tooltip>
  );

  const moreInfoTooltip = (props) => (
    <Tooltip id="delivery-info-tooltip" className="location-tooltip location-tooltip-delivery" {...props}>
      <div className="location-tooltip-heading">Delivery windows</div>
      {isMiami ? (
        <div className="location-tooltip-line">10:00 AM - 6:00 PM.</div>
      ) : (
        <>
          <div className="location-tooltip-line">Weekdays - 8am-8pm,</div>
          <div className="location-tooltip-line">Weekends - 8am-6pm.</div>
        </>
      )}
      <div className="location-tooltip-paragraph">
        For deliveries outside these time windows, please contact our office for arrangements (additional charges will apply).
      </div>
      {!isMiami && (
        <>
          <div className="location-tooltip-heading location-tooltip-heading-spaced">
            Complimentary delivery
          </div>
          <div className="location-tooltip-paragraph">
            {TOOLTIP_MESSAGE.DROP_OFF_LOCATION_2}
          </div>
        </>
      )}
    </Tooltip>
  );

  // No overlay wrapper on desktop - an unstyled one still counts as a flex
  // item of .location-field, stealing a `gap` from the trigger next to it.
  const popoverBody = (
    <div
      className={`location-popover ${mobileSheet ? "mobile-location-sheet" : ""} ${placement === "below" ? "location-popover--below" : ""}`}
      ref={popoverRef}
    >
      <div className="location-popover-body">
        <div className="location-popover-header">
          <h4>Location</h4>
          <button
            type="button"
            className="location-popover-close"
            onClick={() => onClose()}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 6L18 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18 6L6.00003 18" stroke="#202124" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {pickupAddress && (
          <div className="location-tabs">
            <button
              type="button"
              className={`location-tab ${draft.activeTab === "pickup" ? "active" : ""
                }`}
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  activeTab: "pickup",
                  pickupAddress,
                  deliveryAddress: "",
                  deliveryStreetAddress: "",
                  deliveryZipcode: "",
                  deliveryCity: "",
                }))
              }
            >
              Pickup
            </button>
            <button
              type="button"
              className={`location-tab ${draft.activeTab === "delivery" ? "active" : ""
                }`}
              onClick={() =>
                setDraft((d) => ({ ...d, activeTab: "delivery" }))
              }
            >
              Delivery
            </button>
          </div>
        )}

        {draft.activeTab === "pickup" ? (
          <div className="location-field-group">
            <label className="location-field-label">Pickup address</label>
            <p className="location-address-static">{pickupAddress}</p>
          </div>
        ) : (
          <div className="location-field-group">
            <label className="location-field-label">
              Delivery address
              <OverlayTrigger
                placement="top"
                overlay={moreInfoTooltip}
                container={popoverRef}
                popperConfig={tooltipPopperConfig()}
              >
                <img
                  src={tooltipIcon}
                  alt="Tooltip"
                  className="location-info-icon"
                />
              </OverlayTrigger>
            </label>
            <ReactGoogleAutocomplete
              key={`delivery-${resetKey}`}
              apiKey={GOOGLE_API_KEY}
              type="text"
              className="location-address-input"
              placeholder="Delivery address"
              defaultValue={draft.deliveryAddress}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  deliveryAddress: e.target.value,
                  deliveryStreetAddress: "",
                  deliveryZipcode: "",
                  deliveryCity: "",
                }))
              }
              onPlaceSelected={(place, inputEl) => {
                const { address, streetAddress, city, zipcode } = buildAddressFromPlace(place);
                if (inputEl) inputEl.value = address;
                setDraft((d) => ({
                  ...d,
                  deliveryAddress: address,
                  deliveryStreetAddress: streetAddress,
                  deliveryZipcode: zipcode,
                  deliveryCity: city,
                }));
              }}
              options={{
                fields: GOOGLE_PLACE.fields,
                types: GOOGLE_PLACE.types,
                componentRestrictions: { country: "us" },
              }}
            />
          </div>
        )}

        <div className="location-one-way-row">
          <span className="location-one-way-label">
            One-way
            <OverlayTrigger
              placement="top"
              overlay={oneWayTooltip}
              container={popoverRef}
              popperConfig={tooltipPopperConfig()}
            >
              <img
                src={tooltipIcon}
                alt="Tooltip"
                className="location-info-icon"
              />
            </OverlayTrigger>
          </span>
          <label className="location-toggle">
            <input
              type="checkbox"
              checked={draft.oneWay}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  oneWay: e.target.checked,
                  returnAddress: e.target.checked ? d.returnAddress : "",
                  returnStreetAddress: e.target.checked ? d.returnStreetAddress : "",
                  returnZipcode: e.target.checked ? d.returnZipcode : "",
                  returnCity: e.target.checked ? d.returnCity : "",
                }))
              }
            />
            <span className="location-toggle-slider"></span>
          </label>
        </div>

        {draft.oneWay && (
          <div className="location-field-group">
            <label className="location-field-label">Return address</label>
            <ReactGoogleAutocomplete
              key={`return-${resetKey}`}
              apiKey={GOOGLE_API_KEY}
              type="text"
              className="location-address-input"
              placeholder="Type return address"
              defaultValue={draft.returnAddress}
              onChange={(e) =>
                setDraft((d) => ({
                  ...d,
                  returnAddress: e.target.value,
                  returnStreetAddress: "",
                  returnZipcode: "",
                  returnCity: "",
                }))
              }
              onPlaceSelected={(place, inputEl) => {
                const { address, streetAddress, city, zipcode } = buildAddressFromPlace(place);
                if (inputEl) inputEl.value = address;
                setDraft((d) => ({
                  ...d,
                  returnAddress: address,
                  returnStreetAddress: streetAddress,
                  returnZipcode: zipcode,
                  returnCity: city,
                }));
              }}
              options={{
                fields: GOOGLE_PLACE.fields,
                types: GOOGLE_PLACE.types,
                componentRestrictions: { country: "us" },
              }}
            />
          </div>
        )}
      </div>

      <div className="location-popover-actions">
        <button
          type="button"
          className="location-reset-btn"
          disabled={!isDirty}
          onClick={handleReset}
        >
          Reset
        </button>
        <button
          type="button"
          className="location-save-btn"
          disabled={!isSaveEnabled}
          onClick={handleSave}
        >
          Save
        </button>
      </div>
    </div>
  );

  return mobileSheet ? (
    <div className="location-popover-overlay">{popoverBody}</div>
  ) : (
    popoverBody
  );
}

export default LocationPopover;
